<script lang="ts" setup>
import { useSdk } from '~/sdk';
import { getPsImage } from '@/composables/useCms';
import { getCmsImage } from '@/composables/useCms';
import { Swiper, SwiperSlide } from 'swiper/vue';
import { Navigation, Pagination } from 'swiper/modules';
import { ChevronRight } from 'lucide-vue-next';

const { $i18n } = useNuxtApp();
const { t } = $i18n;
const props = defineProps<{
  id: string;
  uid: string;
  title: string;
  slug: string;
  body: string;
  categories: { title: string; slug: string; uri: string; image: { title: string; url: string }[] }[];
}>();

const localePath = useLocalePath();

// Get the first category image if available
const categoryImage = computed(() => {
  const firstCategory = props.categories[0];
  if (firstCategory?.image && firstCategory.image.length > 0) {
    return firstCategory.image[0];
  }
  return null;
});

const state = useState<{
  products: any[];
  loading: boolean;
  error: any;
}>(props.uid, () => ({
  products: [],
  loading: false,
  error: null,
}));

async function fetch() {
  state.value.loading = true;
  state.value.error = null;

  const res = await useSdk().commerce.search({
    s: '',
    page: 1,
    facets: { categories: [props.categories[0]?.slug ?? ''] },
  });

  if (res.products) {
    state.value.products = res.products;
  }

  state.value.loading = false;
}

await fetch();
</script>

<template>
  <VSection>
    <v-container>
      <div class="flex flex-col gap-6 mx-auto">
        <h2 class="text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900">
          {{ title }}
        </h2>

        <div v-if="body" v-html="body" class="prose prose-lg text-gray-700 max-w-none" />

        <!-- Grid Layout: Category Image + Product Cards -->
        <div class="grid grid-cols-1 lg:grid-cols-4 gap-6 mb-10">
          <!-- First Column: Category Image -->
          <div class="lg:col-span-1">
            <div v-if="categoryImage" class="relative h-full">
              <NuxtImg
                :src="getCmsImage([categoryImage])"
                :alt="categoryImage.title || categories[0]?.title"
                :title="categoryImage.title || categories[0]?.title"
                format="webp"
                loading="lazy"
                class="w-full h-full max-h-87.5 lg:max-h-full rounded-lg shadow-lg lg:aspect-3/4 object-cover"
              />
              <div class="absolute inset-0 bg-linear-to-t from-black/60 to-transparent rounded-lg flex items-end">
                <div class="flex flex-col gap-4 p-4 text-white">
                  <h3 class="text-2xl font-semibold">{{ categories[0]?.title }}</h3>
                  <NuxtLink class="flex py-3 gap-2" :to="localePath(`/${props.categories[0]?.uri}`)">
                    <span class="underline">{{ t('View all') }}</span>
                    <ChevronRight class="size-6" />
                  </NuxtLink>
                </div>
              </div>
            </div>
          </div>

          <!-- Rest of columns: Product Cards Carousel -->
          <div class="lg:col-span-3">
            <Swiper
            class="pb-10"
            :autoHeight="true"
              :breakpoints="{
                640: {
                  slidesPerView: 1,
                },
                768: {
                  slidesPerView: 2,
                },
                1024: {
                  slidesPerView: 2,
                },
                1280: {
                  slidesPerView: 3,
                },
              }"
              :space-between="24"
              navigation
              :pagination="{ clickable: true }"
              :modules="[Navigation, Pagination]"
            >
              <SwiperSlide v-for="product in state.products" :key="product.id">
                <VProductCard :product="product" :title="product.name" :image="getPsImage(product.primaryImage)" />
              </SwiperSlide>
            </Swiper>
          </div>
        </div>
      </div>
    </v-container>
  </VSection>
</template>
